import { zodResolver } from '@hookform/resolvers/zod';
import { forgotMfaSchema, mfaCodeSchema } from '@willnq/shared/validation/authSchemas';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { authService } from '@/services/authService';
import { parseRestError } from '@/utils/errorUtils';

type ForgotMfaFormValues = z.infer<typeof forgotMfaSchema>;

const otpSchema = z.object({ otp: mfaCodeSchema });
type OtpFormValues = z.infer<typeof otpSchema>;

const emailOnlySchema = z.object({ email: z.string().email() });
type EmailOnlyFormValues = z.infer<typeof emailOnlySchema>;

export type ForgotMfaStage = 'email' | 'method' | 'password' | 'otp' | 'confirm' | 'done';

/**
 * Drives the pre-authentication lost-authenticator reset (`ForgotMfaForm`) — for a caller who cannot sign
 * in at all (no code they have works), so the authenticated `ResetMfaDialog` is unreachable. Stages:
 *
 *  1. `email`    — the address, which only the WillnQ-password route needs.
 *  2. `method`   — WHICH credential, chosen before either is shown. Nothing is asked for on this screen
 *                  and no request is made: the server is never asked which credential an address uses,
 *                  because answering that for any address supplied is an account-enumeration oracle.
 *  3a. `password` — the WillnQ password field, rendered ONLY once chosen. A provider-created account has
 *                  no WillnQ password at all, so showing this field to everyone up front funnelled exactly
 *                  the people this flow exists for into a route that cannot succeed for them.
 *  3b. `confirm`  — Google route: the explicit "reset your authenticator?" gate. Reaching it means a Google
 *                  identity was verified against a real account, and nothing has been changed yet.
 *  4. `otp`      — password route only: spend the code emailed after the password was accepted.
 *  5. `done`     — MFA is off; the wording depends on which route got here.
 */
export function useForgotMfa() {
  const [stage, setStage] = useState<ForgotMfaStage>('email');
  const [email, setEmail] = useState('');
  const [resetVia, setResetVia] = useState<'password' | 'google'>('password');
  const [serverError, setServerError] = useState<string | null>(null);

  const emailForm = useForm<EmailOnlyFormValues>({
    resolver: zodResolver(emailOnlySchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: { email: '' },
  });

  const passwordForm = useForm<ForgotMfaFormValues>({
    resolver: zodResolver(forgotMfaSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: { email: '', password: '' },
  });

  const otpForm = useForm<OtpFormValues>({
    resolver: zodResolver(otpSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: { otp: '' },
  });

  /** The address stage's only job: carry the address into the password form, which submits it. */
  const onSubmitEmail = emailForm.handleSubmit((values) => {
    setServerError(null);
    setEmail(values.email);
    passwordForm.setValue('email', values.email);
    setStage('method');
  });

  /** Returns to the method choice, clearing anything typed on the route being abandoned. */
  const backToMethod = () => {
    setServerError(null);
    passwordForm.setValue('password', '');
    otpForm.reset({ otp: '' });
    setStage('method');
  };

  const onSubmitPassword = passwordForm.handleSubmit(async (values) => {
    setServerError(null);
    try {
      await authService.forgotMfa(values.email, values.password);
      setEmail(values.email);
      setResetVia('password');
      setStage('otp');
    } catch (error) {
      // A validation/rate-limit failure surfaces here; the "no such account"/"wrong password"/"this
      // account has no WillnQ password" cases never do — the server always resolves for those (see the
      // handler's own doc comment), which is why the next screen must not claim a code was definitely
      // sent. It cannot know.
      setServerError(parseRestError(error).message);
    }
  });

  const onSubmitOtp = otpForm.handleSubmit(async (values) => {
    setServerError(null);
    try {
      await authService.resetMfa({ email, otp: values.otp });
      setResetVia('password');
      setStage('done');
    } catch (error) {
      setServerError(parseRestError(error).message);
    }
  });

  return {
    stage,
    setStage,
    email,
    setEmail,
    resetVia,
    setResetVia,
    emailForm,
    passwordForm,
    otpForm,
    onSubmitEmail,
    onSubmitPassword,
    onSubmitOtp,
    backToMethod,
    serverError,
  };
}
